/**
 * Subscribes to disposition and past-due socket events and updates UI (toasts, refetch, counts).
 * Aligned with SOCKET_DISPOSITION_REALTIME.md and SOCKET_PAST_DUE_REALTIME.md.
 */
import { useEffect, useRef } from "react";
import { onSocket, offSocket, getSocketOrNull } from "../services/socket";
import { requestOpportunitiesRefetch } from "../redux/slices/OpportunitiesSlice";
import { useAppSelector } from "../redux/hooks";
import { toast } from "sonner";
import type { AppDispatch } from "../redux/store";
import type {
  DispositionCreatedPayload,
  LeadMovedTodayContactedPayload,
  LeadListRefreshPayload,
  LeadCountsUpdatedPayload,
  LeadMovedPastDuePayload,
  LeadPastDueAddedPayload,
} from "../types/socketEvents";

/** Custom event name for lead counts (MyLeadsTabs listens) */
export const LEAD_COUNTS_UPDATED_EVENT = "lead:counts:updated";
/** Custom event name to request a counts refetch (MyLeadsTabs listens) */
export const LEAD_COUNTS_REFRESH_EVENT = "lead:counts:refresh";

/** Collapse lead:list:refresh bursts into one refetch request. */
const LEAD_LIST_REFRESH_REFETCH_DEBOUNCE_MS = 400;

/** Collapse rapid lead:moved:past-due bursts (batch past-due check) into one toast. */
const PAST_DUE_TOAST_DEBOUNCE_MS = 1500;

/** Notify MyLeadsTabs to update badge counts (custom DOM event) */
function emitLeadCountsUpdated(counts: LeadCountsUpdatedPayload["counts"]) {
  try {
    window.dispatchEvent(
      new CustomEvent(LEAD_COUNTS_UPDATED_EVENT, { detail: counts })
    );
  } catch {
    // ignore
  }
}

/** Request MyLeadsTabs to refetch counts from API (custom DOM event) */
export function emitLeadCountsRefresh(): void {
  try {
    window.dispatchEvent(new CustomEvent(LEAD_COUNTS_REFRESH_EVENT));
  } catch {
    // ignore
  }
}

export function useDispositionRealtime(dispatch: AppDispatch): void {
  const currentUserId = useAppSelector((state) => state.userProfile?.user?.id);
  const leadListRefreshDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const pastDueToastDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const pastDueBatchRef = useRef<{
    opportunityIds: Set<string>;
    reason: string | undefined;
  }>({
    opportunityIds: new Set(),
    reason: undefined,
  });

  useEffect(() => {
    const socket = getSocketOrNull();
    if (!socket) return;

    const scheduleLeadListRefreshRefetch = () => {
      if (leadListRefreshDebounceRef.current) {
        clearTimeout(leadListRefreshDebounceRef.current);
      }
      leadListRefreshDebounceRef.current = setTimeout(() => {
        leadListRefreshDebounceRef.current = null;
        dispatch(requestOpportunitiesRefetch());
      }, LEAD_LIST_REFRESH_REFETCH_DEBOUNCE_MS);
    };

    const flushPastDueToast = () => {
      pastDueToastDebounceRef.current = null;
      const batch = pastDueBatchRef.current;
      const count = batch.opportunityIds.size;
      if (count === 0) return;

      const reason = batch.reason ?? "Incomplete";

      pastDueBatchRef.current = {
        opportunityIds: new Set(),
        reason: undefined,
      };

      const message =
        count === 1
          ? `Lead moved to past-due: ${reason}.`
          : `Leads moved to past-due: ${reason}.`;

      toast.error(message, { duration: 8000 });
    };

    const schedulePastDueToast = (data: LeadMovedPastDuePayload) => {
      const batch = pastDueBatchRef.current;
      const opportunityId = data.opportunityId;

      if (opportunityId) {
        if (batch.opportunityIds.has(opportunityId)) {
          // Same lead re-emitted within the window — keep one entry only.
          if (!pastDueToastDebounceRef.current) {
            pastDueToastDebounceRef.current = setTimeout(
              flushPastDueToast,
              PAST_DUE_TOAST_DEBOUNCE_MS
            );
          }
          return;
        }
        batch.opportunityIds.add(opportunityId);
      } else if (batch.opportunityIds.size === 0) {
        // No id — still count as one anonymous event for the batch message.
        batch.opportunityIds.add("anonymous-0");
      }

      if (!batch.reason && data.reason) {
        batch.reason = data.reason;
      }

      if (pastDueToastDebounceRef.current) {
        clearTimeout(pastDueToastDebounceRef.current);
      }
      pastDueToastDebounceRef.current = setTimeout(
        flushPastDueToast,
        PAST_DUE_TOAST_DEBOUNCE_MS
      );
    };

    // Backend emits disposition:created to tenant/patient/opportunity rooms; only toast for the actor.
    const recentDispositionToastKeys = new Set<string>();

    // --- Today-contacted (5 events) ---
    const onDispositionCreated = (data: DispositionCreatedPayload) => {
      if (!currentUserId || data.changedBy !== currentUserId) return;

      const dedupeKey =
        data.dispositionId ??
        `${data.opportunityId}-${data.changedAt ?? data.timestamp ?? ""}`;
      if (recentDispositionToastKeys.has(dedupeKey)) return;
      recentDispositionToastKeys.add(dedupeKey);
      setTimeout(() => recentDispositionToastKeys.delete(dedupeKey), 2000);

      toast.success(`Disposition added: ${data.disposition ?? "Contacted"}`);
    };

    const onLeadMovedTodayContacted = (
      _data: LeadMovedTodayContactedPayload
    ) => {
      toast.success("Lead moved to today contacted!");
    };

    const onLeadListRefresh = (_data: LeadListRefreshPayload) => {
      scheduleLeadListRefreshRefetch();
    };

    const onLeadTodayContactedAdded = (
      _data: LeadMovedTodayContactedPayload
    ) => {
      // List will refetch via lead:list:refresh; no need to prepend here unless we add optimistic add
    };

    const onLeadCountsUpdated = (data: LeadCountsUpdatedPayload) => {
      emitLeadCountsUpdated(data.counts);
    };

    // --- Past-due ---
    // CAR-2856: incomplete-disposition warning is no longer a socket event.
    // The submitter toast comes from POST /dispositions `meta.incompleteDisposition`.

    const onLeadMovedPastDue = (data: LeadMovedPastDuePayload) => {
      schedulePastDueToast(data);
    };

    const onLeadPastDueAdded = (_data: LeadPastDueAddedPayload) => {
      // List refresh will refetch past-due tab
    };

    const onLeadPastDueCreated = () => {
      // Tenant-wide; optional dashboard use
    };

    onSocket("disposition:created", onDispositionCreated);
    onSocket("lead:moved:today-contacted", onLeadMovedTodayContacted);
    onSocket("lead:list:refresh", onLeadListRefresh);
    onSocket("lead:today-contacted:added", onLeadTodayContactedAdded);
    onSocket("lead:counts:updated", onLeadCountsUpdated);
    onSocket("lead:moved:past-due", onLeadMovedPastDue);
    onSocket("lead:past-due:added", onLeadPastDueAdded);
    onSocket("lead:past-due:created", onLeadPastDueCreated);

    return () => {
      if (leadListRefreshDebounceRef.current) {
        clearTimeout(leadListRefreshDebounceRef.current);
        leadListRefreshDebounceRef.current = null;
      }
      if (pastDueToastDebounceRef.current) {
        clearTimeout(pastDueToastDebounceRef.current);
        pastDueToastDebounceRef.current = null;
      }
      pastDueBatchRef.current = {
        opportunityIds: new Set(),
        reason: undefined,
      };
      offSocket("disposition:created", onDispositionCreated);
      offSocket("lead:moved:today-contacted", onLeadMovedTodayContacted);
      offSocket("lead:list:refresh", onLeadListRefresh);
      offSocket("lead:today-contacted:added", onLeadTodayContactedAdded);
      offSocket("lead:counts:updated", onLeadCountsUpdated);
      offSocket("lead:moved:past-due", onLeadMovedPastDue);
      offSocket("lead:past-due:added", onLeadPastDueAdded);
      offSocket("lead:past-due:created", onLeadPastDueCreated);
    };
  }, [dispatch, currentUserId]);
}
